import React from "react";
import { motion } from "framer-motion";
import { isTBD } from "../../data/hackathon";
import "./hk-sections.scss";

export const EASE = [0.2, 0.7, 0.1, 1];

/** Six card accents, cycled so no two neighbouring cards share a colour. */
export const ACCENTS = [
  { c: "#2563eb", s: "#eaf1ff" },
  { c: "#ea7317", s: "#fff2e3" },
  { c: "#7c3aed", s: "#f2ecff" },
  { c: "#15803d", s: "#e6f6ec" },
  { c: "#db2777", s: "#ffe9f3" },
  { c: "#0d9488", s: "#e1f6f3" },
];

/**
 * The six stage tones from the "How it Works" notebook. Reused across the page
 * so a card, a tape strip and a stamp all share one colour family.
 */
export const TONES = [
  { t: "#0041e8", s: "#e8efff" },
  { t: "#0b8a83", s: "#e2f4f1" },
  { t: "#6d3fe0", s: "#efe9ff" },
  { t: "#e05a12", s: "#fff0e5" },
  { t: "#d0306f", s: "#fde9f1" },
  { t: "#c77700", s: "#fff3d9" },
];

/**
 * Inline custom properties that give one card its notebook tone.
 *
 * `--tone` is picked for borders, tape and tints, where it only has to read as
 * a colour. Several of the six fall under 4.5:1 on paper once they carry small
 * text (teal 4.15, orange 3.66, amber 3.40), so `--tone-ink` is the same hue
 * pulled towards the page's ink — every tone clears 5:1 there. Use it for any
 * label, tag or caption, and keep `--tone` for the decoration.
 */
export const tone = (i) => {
  const a = TONES[i % TONES.length];
  return {
    "--tone": a.t,
    "--tint": a.s,
    "--tone-ink": `color-mix(in srgb, ${a.t} 78%, #0a0f1e)`,
  };
};

/** Inline custom properties that colour one card. */
export const accent = (i) => {
  const a = ACCENTS[i % ACCENTS.length];
  return { "--accent": a.c, "--accent-soft": a.s };
};

/** Short fade-up as a block enters the viewport. */
export const rise = (delay = 0, y = 16) => ({
  initial: { opacity: 0, y },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "0px 0px -10% 0px" },
  transition: { duration: 0.5, ease: EASE, delay },
});

/** Shows a value, or a "TBD" / "Coming soon" marker when it isn't confirmed. */
export function Value({ v, fallback = "TBD" }) {
  return isTBD(v) ? <span className="hk-tbd">{v || fallback}</span> : <>{v}</>;
}

/** Renders "Team format: TBD." with the TBD part marked up. */
export function withTbd(text) {
  return String(text)
    .split(/\b(TBD|Coming soon)\b/)
    .map((part, i) => (i % 2 ? <span key={i} className="hk-tbd">{part}</span> : part));
}

/**
 * A faint field of doodles behind a block — the same stickers-on-paper idea as
 * the notebook pages. Purely decorative, so it is always aria-hidden.
 */
export function Doodles({ icons }) {
  return (
    <div className="hk-doodles" aria-hidden="true">
      {icons.map((D, i) => (
        <span key={i} className={`hk-doodle hk-doodle--${i + 1}`}>
          <D strokeWidth={1.4} />
        </span>
      ))}
    </div>
  );
}

/**
 * Centred section heading: an optional taped paper label, the tricolour marker,
 * then a two-tone title, then the lead. `title` is [plain, highlighted]. The
 * dash rule only appears between two complete sentences — "Find a real problem.
 * — Build a real solution." — never mid-phrase.
 */
export function Head({ id, chip, title, sub }) {
  const [plain, highlight] = title;
  const dash = Boolean(highlight) && plain.trim().endsWith(".");

  return (
    <motion.div className="hk-top" {...rise()}>
      {chip && <span className="hk-chip">{chip}</span>}
      <span className="hk-flag" aria-hidden="true"><span /><span /><span /></span>
      <h2 className="hk-h2" id={id}>
        {plain}
        {dash && <i aria-hidden="true" />}
        {highlight && <>{dash ? "" : " "}<span>{highlight}</span></>}
      </h2>
      {sub && <p className="hk-sub">{sub}</p>}
    </motion.div>
  );
}
